import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { agentApi } from "../services/agentApi";

export default function AgentChannels({ noPadding = false, readOnly = false }) {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    label: "",
    phoneNumber: "",
    wasenderToken: "",
    isDefault: false,
  });
  const [statusInfo, setStatusInfo] = useState(null);
  const [testSendModal, setTestSendModal] = useState(null);
  const [testSendForm, setTestSendForm] = useState({ to: "", text: "" });
  const [testSendResult, setTestSendResult] = useState(null);
  const [testFlowModal, setTestFlowModal] = useState(false);
  const [testFlowForm, setTestFlowForm] = useState({ phone: "", channelId: "" });
  const [testFlowResult, setTestFlowResult] = useState(null);
  const [testFlowHealth, setTestFlowHealth] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { channels } = await agentApi.channels.list();
      setChannels(channels);
      setError(null);
    } catch (err) {
      if (err?.status === 401 || err?.message?.includes("401") || err?.message?.toLowerCase().includes("unauthorized")) {
        setError("Sessão expirada — faça login novamente.");
      } else {
        setError(err.message || "Erro ao carregar canais");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await agentApi.channels.create(form);
      setShowModal(false);
      setForm({ label: "", phoneNumber: "", wasenderToken: "", isDefault: false });
      await load();
    } catch (err) {
      setError(err.message || "Erro ao criar canal");
    }
  }

  async function handleCheckStatus(id) {
    setStatusInfo({ loading: true, channelId: id });
    try {
      const info = await agentApi.channels.status(id);
      setStatusInfo({ channelId: id, status: info.status });
      await load();
    } catch (err) {
      setStatusInfo({ channelId: id, error: err.message || "Erro ao verificar status" });
    }
  }

  function openTestSend(channel) {
    setTestSendModal(channel);
    setTestSendForm({ to: "", text: "" });
    setTestSendResult(null);
  }

  async function handleTestSend(e) {
    e.preventDefault();
    setTestSendResult({ loading: true });
    try {
      const result = await agentApi.channels.testSend(testSendModal.id, testSendForm);
      setTestSendResult({ ok: true, data: result });
    } catch (err) {
      setTestSendResult({ error: err.message || "Erro ao enviar" });
    }
  }

  async function handleDelete(id) {
    if (!confirm("Remover este canal?")) return;
    await agentApi.channels.delete(id);
    await load();
  }

  async function openTestFlow() {
    const preferred =
      channels.find((c) => c.isDefault) ||
      channels.find((c) => c.status === "CONNECTED") ||
      channels[0];
    setTestFlowForm({ phone: "", channelId: preferred?.id || "" });
    setTestFlowResult(null);
    setTestFlowHealth({ loading: true });
    setTestFlowModal(true);
    try {
      const health = await agentApi.health();
      setTestFlowHealth(health);
    } catch (err) {
      setTestFlowHealth({
        status: "degraded",
        redis: {
          ok: false,
          hint: "Não foi possível verificar se o agente está pronto para enviar mensagens. Tente novamente em instantes ou fale com o time técnico.",
        },
        readyForAgent: false,
        error: err?.message,
      });
    }
  }

  async function handleTestFlow(e) {
    e.preventDefault();
    setTestFlowResult({ loading: true });
    try {
      const payload = {};
      if (testFlowForm.phone.trim()) payload.phone = testFlowForm.phone.trim();
      if (testFlowForm.channelId) payload.channelId = testFlowForm.channelId;
      const result = await agentApi.sessions.test(payload);
      setTestFlowResult({ ok: true, data: result });
    } catch (err) {
      const data = err?.response?.data;
      const apiError = typeof data?.error === "string" ? data.error : "";
      const apiMessage = typeof data?.message === "string" ? data.message : "";
      const msg = apiError || err?.message || "";

      if (apiError === "queue unavailable" || msg.includes("timed out") || msg.includes("Redis")) {
        setTestFlowResult({
          error:
            apiMessage ||
            "O serviço de mensagens está indisponível no momento. Peça ao time técnico para verificar o ambiente do agente e tente de novo.",
          sessionId: data?.sessionId,
        });
      } else if (msg.includes("no test phone"))
        setTestFlowResult({ error: "Nenhum número de teste informado (defina AGENT_TEST_PHONE ou digite um número)." });
      else if (msg.includes("connected channel"))
        setTestFlowResult({ error: "Nenhum canal WhatsApp conectado." });
      else if (msg.includes("active playbook"))
        setTestFlowResult({ error: "Nenhum playbook ativo configurado." });
      else setTestFlowResult({ error: apiMessage || msg || "Erro ao iniciar teste" });
    }
  }

  function maskToken(token) {
    if (!token || token.length < 4) return "••••";
    return "•••••" + token.slice(-4);
  }

  return (
    <div className={noPadding ? "" : "p-6 bg-gray-50 min-h-screen"}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Canais WhatsApp</h1>
          <p className="text-sm text-gray-500 mt-1">Gerencie os números conectados ao agente</p>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <button
              onClick={openTestFlow}
              className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium transition-colors"
            >
              Testar fluxo do agente
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium transition-colors"
            >
              Conectar novo número
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500">Carregando...</div>
      ) : channels.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <div className="text-gray-400 text-sm">Nenhum canal conectado.</div>
          <div className="text-gray-400 text-xs mt-1">Clique em "Conectar novo número" para começar.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {channels.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
            >
              <div>
                <div className="text-gray-900 font-medium flex items-center gap-2">
                  {c.label}
                  {c.isDefault && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      padrão
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 mt-0.5">{c.phoneNumber}</div>
                <div className="text-xs text-gray-400 mt-1">
                  Token: <span className="font-mono">{maskToken(c.wasenderToken)}</span>
                </div>
                <div className="text-xs mt-0.5">
                  Status: <span className={statusColor(c.status)}>{c.status}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleCheckStatus(c.id)}
                  className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm rounded-md transition-colors"
                >
                  Verificar status
                </button>
                {!readOnly && (
                  <>
                    <button
                      onClick={() => openTestSend(c)}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md transition-colors"
                    >
                      Enviar teste
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="px-3 py-1.5 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-sm rounded-md transition-colors"
                    >
                      Remover
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {statusInfo && (
        <Modal title="Status do canal" onClose={() => setStatusInfo(null)}>
          {statusInfo.loading && <div className="text-gray-600">Verificando...</div>}
          {statusInfo.status && (
            <div className={`text-lg font-semibold ${statusColor(statusInfo.status.toUpperCase())}`}>
              {statusInfo.status}
            </div>
          )}
          {statusInfo.error && (
            <div className="text-red-600 text-sm">{statusInfo.error}</div>
          )}
        </Modal>
      )}

      {showModal && (
        <Modal title="Novo canal" onClose={() => setShowModal(false)}>
          <form onSubmit={handleCreate} className="space-y-3">
            <Field
              label="Rótulo"
              value={form.label}
              onChange={(v) => setForm({ ...form, label: v })}
              required
            />
            <Field
              label="Número (E.164, ex: +5511999999999)"
              value={form.phoneNumber}
              onChange={(v) => setForm({ ...form, phoneNumber: v })}
              required
            />
            <Field
              label="Wasender Personal Access Token"
              value={form.wasenderToken}
              onChange={(v) => setForm({ ...form, wasenderToken: v })}
              required
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Definir como padrão
            </label>
            <button
              type="submit"
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
            >
              Criar canal
            </button>
          </form>
        </Modal>
      )}

      {testFlowModal && (
        <Modal title="Testar fluxo do agente" onClose={() => setTestFlowModal(false)}>
          <form onSubmit={handleTestFlow} className="space-y-3">
            <p className="text-sm text-gray-600">
              Inicia uma conversa real do agente (com todas as steps do playbook
              ativo) para um número de teste. Responda pelo WhatsApp do número
              para avançar o fluxo.
            </p>

            {testFlowHealth?.loading ? (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-2">
                Verificando se o agente está pronto...
              </div>
            ) : testFlowHealth && !testFlowHealth.redis?.ok ? (
              <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2 space-y-1">
                <div className="font-medium">Teste temporariamente indisponível</div>
                <div>
                  {testFlowHealth.redis?.hint ||
                    "O envio automático de mensagens não está disponível agora. Peça ao time técnico para verificar o ambiente do agente e tente novamente."}
                </div>
              </div>
            ) : testFlowHealth?.readyForAgent ? (
              <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2">
                Tudo certo — você já pode iniciar o teste do fluxo.
              </div>
            ) : null}

            <Field
              label="Número de teste — deixe em branco para usar o Número de Teste Padrão"
              value={testFlowForm.phone}
              onChange={(v) => setTestFlowForm({ ...testFlowForm, phone: v })}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Iniciar a partir do número
              </label>
              {channels.length === 0 ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                  Nenhum canal cadastrado.
                </div>
              ) : (
                <select
                  value={testFlowForm.channelId}
                  onChange={(e) => setTestFlowForm({ ...testFlowForm, channelId: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:border-blue-500"
                >
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label || c.phoneNumber || "Canal sem nome"}
                      {c.phoneNumber ? ` — ${c.phoneNumber}` : ""}
                      {c.isDefault ? " (canal padrão)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <button
              type="submit"
              disabled={testFlowHealth?.loading || testFlowHealth?.redis?.ok === false}
              className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors"
            >
              Iniciar teste
            </button>
          </form>
          {testFlowResult && (
            <div className="mt-3">
              {testFlowResult.loading && (
                <div className="text-gray-500 text-sm">Iniciando...</div>
              )}
              {testFlowResult.ok && (
                <div className="text-emerald-700 text-sm">
                  Teste iniciado para {testFlowResult.data.phone}!
                  <button
                    onClick={() =>
                      navigate(`/atendimento/conversas?session=${testFlowResult.data.sessionId}`)
                    }
                    className="mt-2 block w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                  >
                    Abrir conversa
                  </button>
                </div>
              )}
              {testFlowResult.error && (
                <div className="text-red-600 text-sm mt-1 space-y-2">
                  <div>{testFlowResult.error}</div>
                  {testFlowResult.sessionId && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/atendimento/conversas?session=${testFlowResult.sessionId}`)
                      }
                      className="block w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                    >
                      Abrir sessão criada
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      {testSendModal && (
        <Modal title={`Enviar teste — ${testSendModal.label}`} onClose={() => setTestSendModal(null)}>
          <form onSubmit={handleTestSend} className="space-y-3">
            <Field
              label="Destinatário (E.164, ex: +5511999999999)"
              value={testSendForm.to}
              onChange={(v) => setTestSendForm({ ...testSendForm, to: v })}
              required
            />
            <Field
              label="Mensagem"
              value={testSendForm.text}
              onChange={(v) => setTestSendForm({ ...testSendForm, text: v })}
              required
            />
            <button
              type="submit"
              className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium transition-colors"
            >
              Enviar
            </button>
          </form>
          {testSendResult && (
            <div className="mt-3">
              {testSendResult.loading && (
                <div className="text-gray-500 text-sm">Enviando...</div>
              )}
              {testSendResult.ok && (
                <div className="text-emerald-700 text-sm">
                  Enviado com sucesso!
                  <pre className="mt-1 text-xs text-gray-500 overflow-auto bg-gray-50 p-2 rounded border border-gray-200">
                    {JSON.stringify(testSendResult.data, null, 2)}
                  </pre>
                </div>
              )}
              {testSendResult.error && (
                <div className="text-red-600 text-sm mt-1">{testSendResult.error}</div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function statusColor(status) {
  if (status === "CONNECTED") return "text-emerald-600 font-medium";
  if (status === "BANNED") return "text-red-600 font-medium";
  return "text-yellow-600 font-medium";
}

function Field({ label, value, onChange, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:border-blue-500"
      />
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-gray-900 font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
